import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api, type AppointmentPreset } from '@/lib/api'
import { ChevronDown, List, Plus, Trash2 } from 'lucide-react'

interface PresetManageCardProps {
  kind: 'note' | 'location'
}

/** 设置页预设管理区块（OPT-022）：集中查看/新增/删除指定类型的预设 */
export function PresetManageCard({ kind }: PresetManageCardProps) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [text, setText] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['appointment-presets', kind],
    queryFn: () => api.getAppointmentPresets(kind),
    staleTime: 30 * 1000,
  })
  const presets = data?.items || []

  const refetch = () => qc.invalidateQueries({ queryKey: ['appointment-presets', kind] })

  const handleAdd = async () => {
    const trimmed = text.trim()
    if (!trimmed) return
    setAdding(true)
    setError('')
    try {
      await api.createAppointmentPreset(kind, trimmed)
      setText('')
      refetch()
    } catch (e) {
      // OPT-040：场地库超档位上限 → 409 提示升级；重复预设保持静默
      const code = (e as Error & { code?: string }).code
      if (code === 'LIMIT_REACHED') setError(t('errors.limitReached'))
    }
    finally { setAdding(false) }
  }

  const handleDelete = async (id: number) => {
    await api.deleteAppointmentPreset(id)
    refetch()
  }

  const title = kind === 'note' ? t('schedule.presets.manageNoteTitle') : t('schedule.presets.manageLocationTitle')

  return (
    <Card data-testid={`preset-manage-card-${kind}`}>
      <CardHeader
        className="cursor-pointer select-none"
        data-testid={`preset-card-toggle-${kind}`}
        onClick={() => setExpanded((v) => !v)}
      >
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <List className="h-4 w-4" /> {title}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent>
          {isLoading && <p className="text-sm text-slate-400">{t('common.loading')}</p>}
          {!isLoading && presets.length === 0 && (
            <p className="text-sm text-slate-400">{t('schedule.presets.empty')}</p>
          )}
          <ul className="space-y-1 text-sm">
            {presets.map((p) => (
              <li key={p.id} className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0">
                <span>{p.text}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid={`preset-card-delete-${p.id}`}
                  onClick={() => handleDelete(p.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          <div className="flex gap-2 mt-3">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('schedule.presets.addPlaceholder')}
              maxLength={200}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd() } }}
              data-testid={`preset-card-input-${kind}`}
            />
            <Button type="button" size="sm" onClick={handleAdd} disabled={adding} data-testid={`preset-card-add-${kind}`}>
              <Plus className="h-3 w-3 mr-1" /> {t('schedule.presets.add')}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  )
}
